import React, { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../../../_redux/attendance/AttendanceAction";
import {
  DeleteButton,
  ExportButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  getDateFormat,
  noRecordsAlert,
  print,
  resultAlert
} from "../../../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody,
  CardHeader,
  CardHeaderToolbar
} from "../../../../../../../_metronic/_partials/controls";
import { StudentsFilter } from "./students-filter/StudentsFilter";
import { StudentsTable } from "./students-table/StudentsTable";
import { useStudentsUIContext } from "./StudentsUIContext";

export function StudentsCard({
  date,
  section,
  courseHour
}) {
  // Students UI Context
  const studentsUIContext = useStudentsUIContext();
  const studentsUIProps = useMemo(() => {
    return {
      ids: studentsUIContext.ids,
      setIds: studentsUIContext.setIds,
      queryParams: studentsUIContext.queryParams
    };
  }, [studentsUIContext]);

  // Getting curret state of Students from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.attendance }),
    shallowEqual
  );  
  const { authorities } = currentState;

  // Authority Parameters
  const { isDeletionAuthorized, isExportationAuthorized } = getAuthorities(authorities);

  // Students Redux state
  const dispatch = useDispatch();

  /** Belli Bir Öğrenci Yoklama Listesini Excele Aktarır */
  const exportAttendancesToExcel = () => {
    dispatch(actions.getAllStudents(studentsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [25, 30, 30, 25, 20];
      const columns = ["Öğrenci Numarası", "Adı Soyadı", "Veli Adı Soyadı", "Veli Telefonu", "Yoklama Durumu"];

      entities.forEach(entity => rows.push([
        entity.studentNumber,
        entity.studentName,
        entity.parentName,
        entity.parentPhone,
        entity.attendanceStatusName
      ]));

      const filename = getDateFormat(date) + " Tarihli " + section + " Sınıfı " + courseHour + ". Ders Öğrenci Yoklama Listesi";
      const sheetname = "Öğrenci Yoklama Listesi"
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Öğrenci Yoklama Listesini PDF e Aktarır */
  const exportAttendancesToPDF = () => {
    dispatch(actions.getAllStudents(studentsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [
        {
          header: "Öğrenci Numarası",
          rows: entities.map(entity => entity.studentNumber)
        },
        {
          header: "Adı Soyadı",
          rows: entities.map(entity => entity.studentName)
        },
        {
          header: "Veli Adı Soyadı",
          rows: entities.map(entity => entity.parentName)
        },
        {
          header: "Veli Telefonu",
          rows: entities.map(entity => entity.parentPhone)
        },
        {
          header: "Yoklama Durumu",
          rows: entities.map(entity => entity.attendanceStatusName)
        }
      ];
  
      const filename = getDateFormat(date) + " Tarihli " + section + " Sınıfı " + courseHour + ". Ders Öğrenci Yoklama Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Öğrenci Yoklama Listesini Yazdırma Penceresini Açar */
  const printAttendances = () => {
    dispatch(actions.getAllStudents(studentsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = getDateFormat(date) + " Tarihli " + section + " Sınıfı " + courseHour + ". Ders Öğrenci Yoklama Listesi";
      const properties = ["Öğrenci Numarası", "Adı Soyadı", "Veli Adı Soyadı", "Veli Telefonu", "Yoklama Durumu"];
  
      entities.forEach(entity => {
        data.push({
          "Öğrenci Numarası": entity.studentNumber,
          "Adı Soyadı": entity.studentName,
          "Veli Adı Soyadı": entity.parentName,
          "Veli Telefonu": entity.parentPhone,
          "Yoklama Durumu": entity.attendanceStatusName
        });
      });
  
      print({ data, properties, header });
    });    
  }

  /** Belli Bir Öğrenci Yoklama Listesini Panoya Kopyalar */
  const copyAttendances = () => {
    dispatch(actions.getAllStudents(studentsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["Öğrenci Numarası", "Adı Soyadı", "Veli Adı Soyadı", "Veli Telefonu", "Yoklama Durumu"];
      const header = getDateFormat(date) + " Tarihli " + section + " Sınıfı " + courseHour + ". Ders Öğrenci Yoklama Listesi";
      entities.forEach(entity => rows.push([
        entity.studentNumber,
        entity.studentName,
        entity.parentName,
        entity.parentPhone,
        entity.attendanceStatusName
      ]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Öğrenci Yoklama Listesini Siler */
  const deleteAttendances = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }
    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deleteAttendances(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
  
    if (isSuccess) {
      setTimeout(() => {
        studentsUIProps.setIds([]);
        dispatch(actions.getStudents(studentsUIProps.queryParams));
      }, 2500);
    }
  };

  const title = <>Öğrenci Yoklama Listesi{studentsUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{studentsUIProps.ids.length}</span>]</> :
                  ""}
                </>;

  return (
    <Card>
      <CardHeader title={title}>
        {
          (isDeletionAuthorized || isExportationAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportAttendancesToExcel}
                exportToPDF={exportAttendancesToPDF}
                print={printAttendances}
                copy={copyAttendances}
              />
            }
            {
              isDeletionAuthorized &&
              <DeleteButton 
                title="Seçilenleri Sil"
                method={() => deleteAttendances(studentsUIProps.ids)}
              />
            }            
          </CardHeaderToolbar>
        }
      </CardHeader>
      <CardBody>
        <StudentsFilter />
        <StudentsTable
          date={date}
          deleteAttendances={deleteAttendances}
        />
      </CardBody>
    </Card>
  );
}